import React, { useState, useMemo } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonList, IonItem, IonLabel, IonBadge, IonIcon,
  IonSpinner, IonButton, IonFabButton,
  IonItemSliding, IonItemOptions, IonItemOption, IonChip, IonSearchbar,
} from '@ionic/react';
import { add, swapHorizontal, remove, cash, addCircle, trash, walletOutline, listOutline, settingsOutline, filterOutline, logInOutline, logOutOutline } from 'ionicons/icons';
import { useEvents, useDeleteEvent, useDeleteTrade } from '../hooks/useInventory';
import { useCashTransactions, useDeleteCashTransaction } from '../hooks/useCash';
import { useHistory } from 'react-router-dom';
import { InventoryEvent, CashTransaction } from '../types';

const EVENT_ICONS: Record<string, string> = {
  add: add,
  trade: swapHorizontal,
  loss: remove,
  sale: cash,
};

const EVENT_COLORS: Record<string, string> = {
  add: 'success',
  trade: 'primary',
  loss: 'danger',
  sale: 'warning',
};

const FILTER_OPTIONS = ['all', 'add', 'trade', 'loss', 'sale', 'deposit', 'withdrawal'] as const;

const FILTER_ICONS: Record<string, string> = {
  all: filterOutline,
  add: add,
  trade: swapHorizontal,
  loss: remove,
  sale: cash,
  deposit: logInOutline,
  withdrawal: logOutOutline,
};

function formatEventDescription(event: InventoryEvent): string {
  switch (event.event_type) {
    case 'add':
      return `Added ${event.card_name}`;
    case 'trade':
      return `Traded ${event.card_name}${event.trade_in_card_name ? ` for ${event.trade_in_card_name}` : ''}`;
    case 'sale':
      return `Sold ${event.card_name}${event.sale_price ? ` for $${event.sale_price.toFixed(2)}` : ''}`;
    case 'loss':
      return `Lost ${event.card_name}`;
    default:
      return event.card_name;
  }
}

interface TradeGroup {
  trade_group_id: string;
  cardsOut: InventoryEvent[];
  cardsIn: InventoryEvent[];
  notes?: string;
  created_at: string;
}

function formatTradeGroupDescription(group: TradeGroup): string {
  const outNames = group.cardsOut.map((e) => e.card_name).join(', ');
  const inNames = group.cardsIn.map((e) => e.card_name).join(', ');
  if (outNames && inNames) {
    return `Traded ${outNames} for ${inNames}`;
  }
  if (outNames) {
    return `Traded away ${outNames}`;
  }
  return `Received ${inNames}`;
}

function formatTradeGroupSubline(group: TradeGroup): string {
  const totalOut = group.cardsOut.length;
  const totalIn = group.cardsIn.length;
  const valueOut = group.cardsOut.reduce((sum, e) => sum + (e.value || 0), 0);
  const valueIn = group.cardsIn.reduce((sum, e) => sum + (e.value || 0), 0);
  const parts: string[] = [];
  if (totalOut > 0) parts.push(`${totalOut} out${valueOut ? ` ($${valueOut.toFixed(2)})` : ''}`);
  if (totalIn > 0) parts.push(`${totalIn} in${valueIn ? ` ($${valueIn.toFixed(2)})` : ''}`);
  return parts.join(' / ');
}

type TimelineItem =
  | { kind: 'event'; id: string; date: string; event: InventoryEvent }
  | { kind: 'cash'; id: string; date: string; txn: CashTransaction }
  | { kind: 'trade_group'; id: string; date: string; group: TradeGroup };

const ActivityLog: React.FC = () => {
  const history = useHistory();
  const [filter, setFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showActionSheet, setShowActionSheet] = useState(false);

  const isCashFilter = filter === 'deposit' || filter === 'withdrawal';
  const isEventFilter = !isCashFilter && filter !== 'all';

  const eventType = isEventFilter ? filter : undefined;
  const { data: eventsData, isLoading: eventsLoading } = useEvents(
    page, 20, eventType, search || undefined,
  );

  const cashType = isCashFilter ? filter : undefined;
  const { data: cashData, isLoading: cashLoading } = useCashTransactions(
    page, 20, cashType,
  );

  const deleteEvent = useDeleteEvent();
  const deleteCashTxn = useDeleteCashTransaction();
  const deleteTrade = useDeleteTrade();

  const isLoading = eventsLoading || cashLoading;

  const timeline = useMemo<TimelineItem[]>(() => {
    if (isCashFilter) {
      return (cashData?.items || []).map((t) => ({
        kind: 'cash' as const,
        id: `cash-${t.id}`,
        date: t.created_at!,
        txn: t,
      }));
    }

    const events = eventsData?.items || [];

    // Group events by trade_group_id
    const tradeGroups = new Map<string, InventoryEvent[]>();
    const ungroupedEvents: InventoryEvent[] = [];

    for (const e of events) {
      if (e.trade_group_id) {
        const group = tradeGroups.get(e.trade_group_id) || [];
        group.push(e);
        tradeGroups.set(e.trade_group_id, group);
      } else {
        ungroupedEvents.push(e);
      }
    }

    const eventItems: TimelineItem[] = ungroupedEvents.map((e) => ({
      kind: 'event' as const,
      id: `event-${e.id}`,
      date: e.created_at!,
      event: e,
    }));

    const tradeGroupItems: TimelineItem[] = Array.from(tradeGroups.entries()).map(
      ([groupId, groupEvents]) => {
        const cardsOut = groupEvents.filter((e) => e.direction === 'out');
        const cardsIn = groupEvents.filter((e) => e.direction === 'in');
        const earliest = groupEvents.reduce(
          (min, e) => (e.created_at! < min ? e.created_at! : min),
          groupEvents[0].created_at!,
        );
        return {
          kind: 'trade_group' as const,
          id: `trade-${groupId}`,
          date: earliest,
          group: {
            trade_group_id: groupId,
            cardsOut,
            cardsIn,
            notes: groupEvents[0].notes,
            created_at: earliest,
          },
        };
      },
    );

    if (isEventFilter) {
      return [...eventItems, ...tradeGroupItems]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }

    // 'all' filter: merge with cash
    const cashItems: TimelineItem[] = (cashData?.items || [])
      .filter((t) => t.transaction_type !== 'sale' && t.transaction_type !== 'trade_cash_in' && t.transaction_type !== 'trade_cash_out')
      .map((t) => ({
        kind: 'cash' as const,
        id: `cash-${t.id}`,
        date: t.created_at!,
        txn: t,
      }));
    return [...eventItems, ...tradeGroupItems, ...cashItems]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [eventsData, cashData, filter, isCashFilter, isEventFilter]);

  const totalPages = isCashFilter
    ? (cashData?.pages || 0)
    : isEventFilter
      ? (eventsData?.pages || 0)
      : Math.max(eventsData?.pages || 0, cashData?.pages || 0);

  const handleDeleteEvent = async (id: number) => {
    await deleteEvent.mutateAsync(id);
  };

  const handleDeleteCashTxn = async (id: number) => {
    await deleteCashTxn.mutateAsync(id);
  };

  const handleDeleteTrade = async (tradeGroupId: string) => {
    await deleteTrade.mutateAsync(tradeGroupId);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary" style={{ position: 'relative' }}>
          <IonTitle>History</IonTitle>
          <IonIcon
            icon={settingsOutline}
            onClick={() => history.push('/settings')}
            style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 24, cursor: 'pointer', color: '#fff', zIndex: 10 }}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonSearchbar
          value={search}
          onIonInput={(e) => { setSearch(e.detail.value || ''); setPage(1); }}
          placeholder="Search by card name, game, or notes"
          debounce={400}
        />
        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 4, padding: '0 16px 8px', overflowX: 'auto' }}>
          {FILTER_OPTIONS.map((opt) => (
            <IonChip
              key={opt}
              color={filter === opt ? 'primary' : 'medium'}
              outline={filter !== opt}
              onClick={() => { setFilter(opt); setPage(1); }}
            >
              <IonIcon icon={FILTER_ICONS[opt]} style={{ fontSize: 16, marginRight: 4 }} />
              <IonLabel>{opt.charAt(0).toUpperCase() + opt.slice(1)}</IonLabel>
            </IonChip>
          ))}
        </div>

        {isLoading ? (
          <div className="ion-padding ion-text-center"><IonSpinner /></div>
        ) : (
          <>
            <IonList>
              {timeline.length > 0 ? (
                timeline.map((item) => {
                  if (item.kind === 'trade_group') {
                    const group = item.group;
                    return (
                      <IonItemSliding key={item.id}>
                        <IonItem>
                          <IonIcon icon={swapHorizontal} slot="start" />
                          <IonLabel>
                            <h2>{formatTradeGroupDescription(group)}</h2>
                            <p>{formatTradeGroupSubline(group)}</p>
                            <p style={{ fontSize: 12 }}>
                              {new Date(group.created_at).toLocaleString()}
                              {group.notes ? ` - ${group.notes}` : ''}
                            </p>
                          </IonLabel>
                          <IonBadge color="primary" slot="end">trade</IonBadge>
                        </IonItem>
                        <IonItemOptions side="end">
                          <IonItemOption color="danger" onClick={() => handleDeleteTrade(group.trade_group_id)}>
                            <IonIcon icon={trash} slot="icon-only" />
                          </IonItemOption>
                        </IonItemOptions>
                      </IonItemSliding>
                    );
                  } else if (item.kind === 'event') {
                    const event = item.event;
                    return (
                      <IonItemSliding key={item.id}>
                        <IonItem>
                          <IonIcon icon={EVENT_ICONS[event.event_type] || add} slot="start" />
                          <IonLabel>
                            <h2>{formatEventDescription(event)}</h2>
                            <p>
                              {event.game_name}
                              {event.quantity > 1 ? ` x${event.quantity}` : ''}
                              {event.value ? ` - $${event.value.toFixed(2)}` : ''}
                            </p>
                            <p style={{ fontSize: 12 }}>
                              {new Date(event.created_at!).toLocaleString()}
                              {event.notes ? ` - ${event.notes}` : ''}
                            </p>
                          </IonLabel>
                          <IonBadge color={EVENT_COLORS[event.event_type] || 'medium'} slot="end">
                            {event.event_type}
                          </IonBadge>
                        </IonItem>
                        <IonItemOptions side="end">
                          <IonItemOption color="danger" onClick={() => handleDeleteEvent(event.id)}>
                            <IonIcon icon={trash} slot="icon-only" />
                          </IonItemOption>
                        </IonItemOptions>
                      </IonItemSliding>
                    );
                  } else {
                    const txn = item.txn;
                    const isDeposit = txn.transaction_type === 'deposit';
                    return (
                      <IonItemSliding key={item.id}>
                        <IonItem>
                          <IonIcon icon={walletOutline} slot="start" />
                          <IonLabel>
                            <h2>
                              {isDeposit ? 'Deposited' : 'Withdrew'}{' '}
                              ${txn.amount.toFixed(2)}
                            </h2>
                            <p style={{ fontSize: 12 }}>
                              {new Date(txn.created_at!).toLocaleString()}
                              {txn.notes ? ` - ${txn.notes}` : ''}
                            </p>
                          </IonLabel>
                          <IonBadge color={isDeposit ? 'success' : 'danger'} slot="end">
                            {txn.transaction_type}
                          </IonBadge>
                        </IonItem>
                        {txn.transaction_type !== 'sale' && (
                          <IonItemOptions side="end">
                            <IonItemOption color="danger" onClick={() => handleDeleteCashTxn(txn.id)}>
                              <IonIcon icon={trash} slot="icon-only" />
                            </IonItemOption>
                          </IonItemOptions>
                        )}
                      </IonItemSliding>
                    );
                  }
                })
              ) : (
                <div className="ion-padding ion-text-center">No events found.</div>
              )}
            </IonList>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
                <IonButton size="small" fill="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </IonButton>
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 14 }}>
                  Page {page} of {totalPages}
                </span>
                <IonButton size="small" fill="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  Next
                </IonButton>
              </div>
            )}
          </>
        )}

        <div
          style={{
            position: 'fixed', bottom: 16, right: 16, zIndex: 1000,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8,
          }}
        >
          {showActionSheet && (
            <>
              <IonButton
                shape="round"
                color="tertiary"
                onClick={() => { setShowActionSheet(false); history.push('/history/new'); }}
              >
                <IonIcon icon={listOutline} slot="start" />
                Record Event
              </IonButton>
              <IonButton
                shape="round"
                color="tertiary"
                onClick={() => { setShowActionSheet(false); history.push('/history/cash'); }}
              >
                <IonIcon icon={walletOutline} slot="start" />
                Cash Transaction
              </IonButton>
            </>
          )}
          <IonFabButton onClick={() => setShowActionSheet((v) => !v)}>
            <IonIcon icon={addCircle} />
          </IonFabButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ActivityLog;
